/**
 * Shared status glyphs and tone mapping so the tree, the jobs table, the run
 * view and the Home cards all read a state the same way.
 */

import {
  Ban,
  CheckCircle2,
  Circle,
  CircleDashed,
  Loader2,
  TriangleAlert,
  XCircle,
} from "lucide-react";
import type { JobState, StageHealth, StageState } from "@/ipc/contract";
import { cn } from "@/lib/utils";

type Tone = "neutral" | "blue" | "green" | "amber" | "red";

export const STAGE_TONE: Record<StageState, Tone> = {
  pending: "neutral",
  running: "blue",
  completed: "green",
  failed: "red",
  skipped: "amber",
  cancelled: "neutral",
};

export const JOB_TONE: Record<JobState | "not_created", Tone> = {
  queued: "neutral",
  running: "blue",
  completed: "green",
  failed: "red",
  cancelled: "neutral",
  not_created: "neutral",
};

export const STAGE_LABEL: Record<StageState, string> = {
  pending: "Pending",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  skipped: "Skipped",
  cancelled: "Cancelled",
};

export const JOB_LABEL: Record<JobState | "not_created", string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
  not_created: "Not created",
};

const TONE_TEXT: Record<Tone, string> = {
  neutral: "text-neutral-400",
  blue: "text-blue-600",
  green: "text-green-600",
  amber: "text-amber-600",
  red: "text-red-600",
};

export function StageGlyph({ state, className }: { state: StageState; className?: string }) {
  const tone = TONE_TEXT[STAGE_TONE[state]];
  const size = cn("h-3.5 w-3.5 shrink-0", tone, className);
  switch (state) {
    case "completed":
      return <CheckCircle2 className={size} />;
    case "running":
      // The only continuous motion in the app; it marks the live stage.
      return <Loader2 className={cn(size, "animate-spin [animation-duration:1.4s]")} />;
    case "failed":
      return <XCircle className={size} />;
    case "cancelled":
      return <Ban className={size} />;
    case "skipped":
      return <CircleDashed className={size} />;
    default:
      return <Circle className={size} />;
  }
}

export function JobGlyph({ state, className }: { state: JobState | "not_created"; className?: string }) {
  const tone = TONE_TEXT[JOB_TONE[state]];
  const size = cn("h-3.5 w-3.5 shrink-0", tone, className);
  switch (state) {
    case "completed":
      return <CheckCircle2 className={size} />;
    case "running":
      return <Loader2 className={cn(size, "animate-spin [animation-duration:1.4s]")} />;
    case "failed":
      return <XCircle className={size} />;
    case "cancelled":
      return <Ban className={size} />;
    default:
      return <Circle className={size} />;
  }
}

export function stageTone(state: StageState): Tone {
  return STAGE_TONE[state];
}

export function jobTone(state: JobState | "not_created"): Tone {
  return JOB_TONE[state];
}

/**
 * What the run is doing while it says nothing.
 *
 * A quiet stage and a wedged one look the same from the outside — no output,
 * no movement — and the difference is nine minutes of somebody's afternoon. It
 * is also trivially measurable from the inside, so the backend samples the
 * process tree once a stage goes silent and this renders the answer.
 *
 * Every figure shown is a measurement. The amber state is reserved for the one
 * conclusion worth interrupting someone for: the process is resident in more
 * memory than the machine has and is therefore paging rather than computing.
 */
export function StageHealthNote({ health }: { health: StageHealth | null }) {
  if (!health) return null;
  const cores = health.cpuPercent == null ? null : (health.cpuPercent / 100).toFixed(1);
  const gb = health.residentMb == null ? null : (health.residentMb / 1024).toFixed(1);
  return (
    <p
      className={cn(
        "flex items-start gap-2 rounded border px-2.5 py-1.5 text-[11px] leading-relaxed",
        health.paging
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-ads-border bg-ads-subtle text-neutral-600",
      )}
    >
      {health.paging ? <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" /> : null}
      <span className="min-w-0">
        {health.note}
        {cores || gb ? (
          <span className="ml-1 text-neutral-400">
            ({[cores ? `${cores} cores` : null, gb ? `${gb} GB` : null].filter(Boolean).join(" · ")}
            )
          </span>
        ) : null}
      </span>
    </p>
  );
}
